/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { compose } from 'recompose';
import { withRouter } from 'react-router';
import {
    string, object, bool, func, shape,
} from 'prop-types';
import { Grid, Typography, Button } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import ShoppingCartSharpIcon from '@material-ui/icons/ShoppingCartSharp';
import BrandLogo from '../BrandLogo';
import { triggerInterstitialLogin } from '../../../../../state/ducks/Member/ducks/Auth/Auth-Actions';
import { trackEvent as track } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getBrand } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import ModalContent from '../ModalWrapper/Partials/ModalContent';
import CheckoutBanner from './Partials/CheckoutBanner/CheckoutBanner';
import { getFeatureFlags } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getOrderId, getIsAuthenticatedStatus } from '../../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { continueToPayment, getCustomerCare } from '../../../../../state/ducks/Common/Common-Selectors';

import { withAuth } from '../../../../../state/ducks/Member/ducks/Auth/Plugin/auth';

const styles = (theme) => ({
    root: {
        borderBottom: `1px solid ${theme.palette?.colorDisabled || '#ccc'}`,
        paddingTop: '10px',
    },
    checkoutHeaderWrapper: {
        width: '980px !important',
        height: 'auto',
        minHeight: '100px',
        margin: '0 auto',
        [theme.breakpoints.down('md')]: {
            justifyContent: 'center',
        },
    },
    brandLogo: {
        alignSelf: 'center',
        marginBottom: '10px',
    },
    checkoutStepsWrapper: {
        fontSize: '20px',
        fontWeight: '700',
        marginTop: '13px',
        paddingTop: '10px',
        borderTop: `1px solid ${theme.palette.colorDisabled || '#ccc'}`,
        display: 'block !important',
        textAlign: 'right',
    },
    staticLink: {
        fontSize: '14px !important',
        color: theme.palette.contentColor || '#666',
        textDecoration: 'underline',
        marginRight: '20px',
        cursor: 'pointer',
        outline: 'none',
    },
    customerService: {
        fontSize: '14px !important',
        color: theme.palette.contentColor || '#666',
    },
    secureCheckout: {
        color: theme.palette.cms?.checkoutHeaderCurrentStepColor || '#324765',
        marginRight: '25px',
        display: 'inline-block',
    },
    cart: {
        color: theme.palette.colorDisabled || '#ccc',
        marginRight: '25px',
        display: 'inline-block',
        cursor: 'pointer',
        outline: 'none',
    },
    shipping: {
        color: theme.palette.colorDisabled || '#ccc',
        marginRight: '25px',
        display: 'inline-block',
        cursor: 'pointer',
        outline: 'none',
    },
    payment: {
        color: theme.palette.colorDisabled || '#ccc',
        display: 'inline-block',
        cursor: 'pointer',
        outline: 'none',
    },
    cartIcon: {
        width: '25px',
        height: '25px',
        background: theme.palette.colorDisabled || '#ccc',
        padding: '4px',
        borderRadius: '50px',
        color: theme.palette.common?.white || '#fff',
        margin: '0px 6px -6px 0px',
    },
    stepDigit: {
        width: '25px',
        height: '25px',
        borderRadius: '50%',
        fontSize: '16px',
        color: theme.palette.common?.white || '#fff',
        lineHeight: '25px',
        textAlign: 'center',
        background: theme.palette.colorDisabled || '#ccc',
        display: 'inline-block',
        margin: '0 6px 0 0',
    },
    secureCheckoutIcon: {
        marginRight: '10px',
    },
    currentStep: {
        color: theme.palette.cms?.checkoutHeaderCurrentStepColor || '#324765',
    },
    currentStepDigit: {
        background: theme.palette.cms?.checkoutHeaderCurrentStepColor || '#324765',
    },
    cartLink: {
        color: theme.palette.colorDisabled || '#ccc',
    },
    logoBrandBtn: {
        display: 'block',
        textAlign: 'left',
        width: '100% !important',
        '&:hover, &:focus, &:active': {
            backgroundColor: 'transparent',
        },
        '& > span': {
            justifyContent: 'unset',
        },
    },
});

const CheckoutHeader = ({
    classes, orderId, currentStep, customerCarePhoneNumber, isContinueToPayment,
    history, actionTriggerInterstitialLogin, isAuthenticated, featureFlags,
    trackEvent, isConfirmationPage, auth,
}) => {
    const [isOpen, setModal] = useState(false);
    const openModal = () => {
        if (isConfirmationPage) {
            history.push('/');
        } else {
            setModal(true);
        }
    };
    const closeModal = () => {
        setModal(false);
    };
    const modalContent = <ModalContent closeModal={closeModal} />;

    const navigate = (clickSrc) => {
        if (clickSrc === 'shoppingcart' && (currentStep === 'shipping' || currentStep === 'payment')) {
            history.push(`/checkout/cart/${orderId}`);
        } else if (clickSrc === 'shipping' && (currentStep === 'shoppingcart' || currentStep === 'payment')) {
            if ((featureFlags['is-interstitial-login-enabled'] || featureFlags['is-native-interstitial-page-enabled']) && !isAuthenticated) {
                actionTriggerInterstitialLogin(history);
            } else {
                history.push(`/checkout/shipping/${orderId}`);
            }
        } else if (clickSrc === 'payment' && isContinueToPayment && (currentStep === 'shoppingcart' || currentStep === 'shipping')) {
            history.push(`/checkout/payment/${orderId}`);
        }
    };

    const signIn = async () => {
        trackEvent({
            eventCategory: 'Sign In', eventAction: 'Login', eventLabel: 'Logon_From_Normal|<<pageType>>',
        });

        await auth.loginByRedirect();
    };

    return (
        <div className={classes.root}>
            <Grid container className={classes.checkoutHeaderWrapper}>
                <Grid item md={4} lg={4} id="brandLogo" className={classes.brandLogo}>
                    <Button
                        disableRipple
                        id="btn-logo-brand"
                        name="logoBrandButton"
                        className={classes.logoBrandBtn}
                        onClick={openModal}
                    >
                        <BrandLogo withLink />
                    </Button>
                </Grid>
                {
                    !isConfirmationPage
                    && (
                        <>
                            <Grid item md={8} lg={8}>
                                <Grid container id="statiContent" justify="flex-end">
                                    {!isAuthenticated && (
                                        <div
                                            role="button"
                                            tabIndex={-1}
                                            onKeyDown={() => signIn()}
                                            onClick={() => signIn()}
                                            className={classes.staticLink}
                                        >
                                            Sign In
                                        </div>
                                    )}
                                    <Link to={`/checkout/cart/${orderId}`} className={classes.staticLink}>Edit Shopping Cart</Link>
                                    <Link to="/" className={classes.staticLink}>Continue Shopping</Link>
                                    <Typography className={classes.customerService}>Customer Service {customerCarePhoneNumber}</Typography>
                                </Grid>
                                <Grid container id="checkoutSteps" className={classes.checkoutStepsWrapper}>
                                    <div
                                        className={classes.secureCheckout}
                                    >
                                        <span className={classes.secureCheckoutIcon}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24"><path fill="#324765" d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-10 0v-4c0-2.206 1.794-4 4-4s4 1.794 4 4v4h-8z" /></svg>
                                        </span>
                                        Secure Checkout
                                    </div>
                                    <div
                                        onClick={() => { navigate('shoppingcart'); }}
                                        onKeyDown={() => {}}
                                        role="link"
                                        tabIndex="0"
                                        className={currentStep === 'shoppingcart' ? `${classes.cart} ${classes.currentStep}` : classes.cart}
                                    >
                                        <ShoppingCartSharpIcon className={currentStep === 'shoppingcart' ? `${classes.cartIcon} ${classes.currentStepDigit}` : classes.cartIcon} />
                                        Cart
                                    </div>
                                    <div
                                        onClick={() => { navigate('shipping'); }}
                                        onKeyDown={() => { }}
                                        role="link"
                                        tabIndex="0"
                                        className={currentStep === 'shipping' ? `${classes.shipping} ${classes.currentStep}` : classes.shipping}
                                    >
                                        <div className={currentStep === 'shipping' ? `${classes.stepDigit} ${classes.currentStepDigit}` : classes.stepDigit}>1</div>
                                        Shipping
                                    </div>
                                    <div
                                        onClick={() => { navigate('payment'); }}
                                        onKeyDown={() => { }}
                                        role="link"
                                        tabIndex="0"
                                        className={currentStep === 'payment' ? `${classes.payment} ${classes.currentStep}` : classes.payment}
                                    >
                                        <div className={currentStep === 'payment' ? `${classes.stepDigit} ${classes.currentStepDigit}` : classes.stepDigit}>2</div>
                                        Payment
                                    </div>
                                </Grid>
                            </Grid>
                            <CheckoutBanner />
                            <ModalWrapper
                                modalTitle="LEAVE CHECKOUT"
                                modalContent={modalContent}
                                isOpen={isOpen}
                                onCloseModal={closeModal}
                                btnClose={false}
                            />
                        </>
                    )
                }

            </Grid>
        </div>
    );
};

CheckoutHeader.propTypes = {
    history: object.isRequired,
    classes: object.isRequired,
    orderId: string.isRequired,
    currentStep: string.isRequired,
    customerCarePhoneNumber: string.isRequired,
    isContinueToPayment: bool.isRequired,
    isAuthenticated: bool,
    actionTriggerInterstitialLogin: func.isRequired,
    featureFlags: shape({
        'is-interstitial-login-enabled': bool,
        'is-native-interstitial-page-enabled': bool,
    }).isRequired,
    trackEvent: func.isRequired,
    isConfirmationPage: bool,
    auth: object.isRequired,
};

CheckoutHeader.defaultProps = {
    isAuthenticated: false,
    isConfirmationPage: false,
};

const mapStateToProps = (state) => {
    if (!state.checkout) {
        return ({
            orderId: '',
            customerCarePhoneNumber: '',
            isContinueToPayment: false,
            isAuthenticated: false,
            featureFlags: {},
            brand: {},
        });
    }

    return ({
        orderId: getOrderId(state),
        customerCarePhoneNumber: getCustomerCare(state),
        isContinueToPayment: continueToPayment(state),
        isAuthenticated: getIsAuthenticatedStatus(state),
        featureFlags: getFeatureFlags(state),
        brand: getBrand(state),
    });
};

const mapDispatchToProps = (dispatch) => ({
    actionTriggerInterstitialLogin: bindActionCreators(triggerInterstitialLogin, dispatch),
    trackEvent: bindActionCreators(track, dispatch),
});

const styledCheckoutHeader = withStyles(styles)(CheckoutHeader);
const enhance = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
    withAuth,
);

export default enhance(styledCheckoutHeader);
